import React, { useEffect, useMemo, useState } from 'react';
import DraggableWindow from './DraggableWindow';
import CustomSelect from '../CustomSelect';
import { clienteretiraService, lojasService } from '../../services/api';

function toYmd(dateStr) {
  if (!dateStr) return '';
  return String(dateStr).replaceAll('-', '');
}

function isFilled(v) {
  return String(v ?? '').trim() !== '';
}

function getEntregaStatus(row) {
  const qtd = parseFloat(row.QTD || 0);
  const qtdEntregue = parseFloat(row.QTDENTREGUE || 0);
  const temDtEntrega = isFilled(row.DTENTREGA);

  if (!temDtEntrega) return { label: 'Pendente', color: '#1d4ed8', bg: '#dbeafe', border: '#bfdbfe' };
  if (qtdEntregue >= qtd && qtd > 0) return { label: 'Entregue', color: '#166534', bg: '#dcfce7', border: '#bbf7d0' };
  return { label: 'Parcial/Verificar', color: '#92400e', bg: '#fffbeb', border: '#fde68a' };
}

function getTransferStatus(sigla) {
  const s = String(sigla || '').trim().toUpperCase();
  if (s === 'P') return { label: 'Transferência Pendente', color: '#1d4ed8', bg: '#dbeafe', border: '#bfdbfe' };
  if (s === 'N') return { label: 'Não Transferido', color: '#92400e', bg: '#fffbeb', border: '#fde68a' };
  if (s === 'S') return { label: 'Transferido', color: '#166534', bg: '#dcfce7', border: '#bbf7d0' };
  return { label: s || '-', color: '#374151', bg: '#f3f4f6', border: '#e5e7eb' };
}

function getTransferStatusByRow(row) {
  if (String(row.RESPONSAVEL || '').toLowerCase() === 'pedsiteloja') {
    return { label: 'Transferido (PedSite)', color: '#166534', bg: '#dcfce7', border: '#bbf7d0' };
  }
  return getTransferStatus(row.FEZTRANSFERENCIA);
}

const WindowClienteRetira = ({ onClose, onMinimize, isMinimized, zIndex, onFocus, userData }) => {
  const [filial, setFilial] = useState(userData.loja_id || 22);
  const [vendedor, setVendedor] = useState(userData?.matricula ? String(userData.matricula) : '');
  const [tipoRetirada, setTipoRetirada] = useState('todas');
  const [lojas, setLojas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [dados, setDados] = useState([]);

  const hoje = useMemo(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  }, []);
  const umMesAtras = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const [dataIni, setDataIni] = useState(umMesAtras);
  const [dataFim, setDataFim] = useState(hoje);

  useEffect(() => {
    const carregarLojas = async () => {
      try {
        const lojasResponse = await lojasService.getLojas();
        setLojas((lojasResponse || []).sort((a, b) => a.numero - b.numero));
      } catch (e) {
        console.error('Erro ao carregar lojas:', e);
      }
    };
    carregarLojas();
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErro('');
    setLoading(true);
    setDados([]);
    try {
      if (!filial) {
        setErro('Selecione a filial.');
        return;
      }
      if (!dataIni || !dataFim) {
        setErro('Informe período.');
        return;
      }
      const rows = await clienteretiraService.listar({
        cdfil: filial,
        dataIniYmd: toYmd(dataIni),
        dataFimYmd: toYmd(dataFim),
        vendedor: vendedor.trim(),
        tipoRetirada
      });
      setDados(Array.isArray(rows) ? rows : []);
      if (!rows || rows.length === 0) {
        setErro('Sem registros para os filtros informados.');
      }
    } catch (err) {
      console.error('Erro ao consultar clienteretira:', err);
      setErro(err?.message || 'Erro ao consultar registros.');
    } finally {
      setLoading(false);
    }
  };

  const resumo = useMemo(() => {
    const base = { total: dados.length, pendente: 0, entregue: 0, parcial: 0, pedsite: 0, transfPendente: 0 };
    dados.forEach((r) => {
      const st = getEntregaStatus(r).label;
      if (st === 'Pendente') base.pendente += 1;
      else if (st === 'Entregue') base.entregue += 1;
      else base.parcial += 1;
      const isPedSite = String(r.RESPONSAVEL || '').toLowerCase() === 'pedsiteloja';
      if (isPedSite) base.pedsite += 1;
      if (!isPedSite && String(r.FEZTRANSFERENCIA || '').toUpperCase() === 'P') base.transfPendente += 1;
    });
    return base;
  }, [dados]);

  return (
    <DraggableWindow
      title="Cliente Retira"
      icon="fa-hand-holding"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={1180}
      initialTop={40}
      initialLeft={180}
      maxHeight="92vh"
    >
      <form onSubmit={handleSubmit} className="consulta-form">
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.8fr 0.7fr 0.8fr 0.8fr auto', gap: 10, alignItems: 'end' }}>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Filial (CDFIL)</label>
            <CustomSelect
              options={lojas.map((loja) => ({ value: loja.numero, label: `${loja.numero} - ${loja.nome}` }))}
              value={filial}
              onChange={setFilial}
              placeholder="Selecione a filial..."
              searchPlaceholder="Buscar loja..."
            />
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Tipo de retirada</label>
            <select value={tipoRetirada} onChange={(e) => setTipoRetirada(e.target.value)}>
              <option value="todas">Todas</option>
              <option value="propria">Retira própria filial</option>
              <option value="outra">Retira outra loja</option>
            </select>
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Vendedor (opcional)</label>
            <input value={vendedor} onChange={(e) => setVendedor(e.target.value)} placeholder="Ex: 4115" />
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Data inicial</label>
            <input type="date" value={dataIni} onChange={(e) => setDataIni(e.target.value)} />
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Data final</label>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          <button type="submit" className="consulta-btn-submit" disabled={loading} style={{ height: 46 }}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'}`}></i> Consultar
          </button>
        </div>
      </form>

      {erro && <div className="consulta-error" style={{ marginTop: 12 }}>{erro}</div>}

      {!loading && dados.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginTop: 12 }}>
          <div className="caixas-badge" style={{ background: '#f3f4f6', color: '#111827', border: '1px solid #e5e7eb' }}>Total: {resumo.total}</div>
          <div className="caixas-badge" style={{ background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' }}>Pendentes: {resumo.pendente}</div>
          <div className="caixas-badge" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>Entregues: {resumo.entregue}</div>
          <div className="caixas-badge" style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>Parcial: {resumo.parcial}</div>
          <div className="caixas-badge" style={{ background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #bae6fd' }}>PedSite: {resumo.pedsite}</div>
          <div className="caixas-badge" style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe' }}>Transf. P: {resumo.transfPendente}</div>
        </div>
      )}

      {loading && (
        <div className="consulta-loading" style={{ marginTop: 12 }}>
          <i className="fas fa-spinner fa-spin"></i> Buscando registros...
        </div>
      )}

      {!loading && dados.length > 0 && (
        <div className="transfer-table-container" style={{ marginTop: 12 }}>
          <table className="transfer-table">
            <thead>
              <tr>
                <th>CDFIL</th>
                <th>Data/Hora</th>
                <th>NFCE</th>
                <th>Vendedor</th>
                <th>Produto</th>
                <th className="text-right">Qtd</th>
                <th className="text-right">Qtd Entregue</th>
                <th>Entrega</th>
                <th>Transf.</th>
                <th>Origem</th>
                <th>Retira Em</th>
              </tr>
            </thead>
            <tbody>
              {dados.map((r, idx) => {
                const entrega = getEntregaStatus(r);
                const transf = getTransferStatusByRow(r);
                const retiraEm = (String(r.LJENTREGA || '0') === '0' || String(r.LJENTREGA) === String(r.CDFIL))
                  ? `Própria loja (${r.CDFIL})`
                  : `Loja ${r.LJENTREGA}`;
                return (
                  <tr key={`${r.NRNFCE}-${r.CDPRODU}-${idx}`}>
                    <td className="is-strong">{r.CDFIL}</td>
                    <td>{r.DATA} {r.HORA || ''}</td>
                    <td>{r.NRNFCE}</td>
                    <td>{r.VENDEDOR}</td>
                    <td>{r.CDPRODU} - {r.NOMEPRO || '-'}</td>
                    <td className="text-right">{r.QTD}</td>
                    <td className="text-right">{r.QTDENTREGUE ?? 0}</td>
                    <td>
                      <span style={{ display: 'inline-flex', padding: '3px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: entrega.bg, color: entrega.color, border: `1px solid ${entrega.border}` }}>
                        {entrega.label}
                      </span>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', padding: '3px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: transf.bg, color: transf.color, border: `1px solid ${transf.border}` }}>
                        {transf.label}
                      </span>
                    </td>
                    <td>{r.LOJATRANSF || '-'}</td>
                    <td>{retiraEm}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DraggableWindow>
  );
};

export default WindowClienteRetira;
